import type {ReactNode} from 'react'
import {Link} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Skeleton} from '@/components/ui/skeleton'
import {Tag} from '@/components/ui/tag'
import {PorQue} from '@/components/ui/por-que'
import {Hint} from '@/components/ui/hint'
import {FichaDatos, type BloqueFicha} from '@/components/ui/ficha-datos'
import {CeldaEstado} from '@/components/ui/celdas-tabla'
import {AccionLigadaLink} from '@/components/accion-ligada-link'
import {SectionCard} from '@/components/section-card'
import {gravedadChipLabel} from '@/components/incidentes/estado-linea'
import {AfectadosManager} from '@/components/afectados-manager'
import {InvestigacionEvidencia} from '@/components/investigacion-evidencia'
import {furatDocumentoUrl, useLesionesCatalog, type Incidente} from '@/hooks/sgsst/incidentes/use-incidentes'
import {
  useInvestigacion,
  useInvestigacionEvidencia,
  informeUrl,
  type Causa,
  type EquipoSeat,
  type Medida,
} from '@/hooks/sgsst/incidentes/use-investigacion'
import {downloadBlob} from '@/lib/api'
import {formatShortDate} from '@/lib/risk'
import {cn} from '@/lib/utils'

function EquipoList({equipo}: {equipo: EquipoSeat[]}) {
  const {t} = useTranslation()
  if (equipo.length === 0) return <p className="text-sm text-muted-foreground">{t('investigacion.equipo.vacio')}</p>
  return (
    <ul className="space-y-2">
      {equipo.map((seat) => (
        <li
          key={seat.id}
          className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border p-3">
          <div className="min-w-0">
            <p className="text-sm font-medium">{t(`investigacion.rol.${seat.rol}`)}</p>
            <div className="text-xs text-muted-foreground">
              {seat.nombre ?? t('investigacion.equipo.sinAsignar')}
              {seat.jefe_porque && <span> · {seat.jefe_porque}</span>}
            </div>
          </div>
          {seat.rol === 'profesional_licencia_so' && seat.nombre !== null && (
            <Tag tone={seat.licencia_ok ? 'green' : 'red'}>
              {t(seat.licencia_ok ? 'investigacion.equipo.licenciaOk' : 'investigacion.equipo.licenciaNoVerificable')}
            </Tag>
          )}
        </li>
      ))}
    </ul>
  )
}

function CausasTree({causas}: {causas: Causa[]}) {
  const {t} = useTranslation()
  if (causas.length === 0) return <p className="text-sm text-muted-foreground">{t('investigacion.causas.vacio')}</p>
  const children = new Map<string | null, Causa[]>()
  for (const c of causas) {
    const key = c.parent_causa_id
    children.set(key, [...(children.get(key) ?? []), c])
  }
  function renderLevel(parentId: string | null, depth: number): ReactNode {
    const nodes = children.get(parentId) ?? []
    return (
      <ul className={cn('space-y-1.5', depth > 0 && 'mt-1.5 border-l border-border pl-3')}>
        {nodes.map((c) => (
          <li key={c.id} data-testid="ficha-causa-row">
            <p className="text-sm">{c.descripcion}</p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {c.tipo_label}
              {c.catalogo_label && <span title={c.catalogo_code ?? undefined}> · {c.catalogo_label}</span>}
            </p>
            {renderLevel(c.id, depth + 1)}
          </li>
        ))}
      </ul>
    )
  }
  return renderLevel(null, 0)
}

/** El vacío no ofrece crear una acción suelta: la Res. 1401/2007 fija el camino investigar → medidas. */
function AccionesPlanLigadas({
  incidente,
  businessId,
  medidas,
  cargando,
}: {
  incidente: Incidente
  businessId: string
  medidas: Medida[] | undefined
  cargando: boolean
}) {
  const {t} = useTranslation()
  if (cargando || !medidas) return <Skeleton className="h-16 w-full" />
  if (medidas.length > 0) {
    return (
      <ul className="space-y-2" data-testid="acciones-plan-lista">
        {medidas.map((m) => (
          <li key={m.id} className="rounded-lg border border-border p-3" data-testid="acciones-plan-row">
            <AccionLigadaLink
              businessId={businessId}
              accion={{id: m.id, descripcion: m.description, estado: m.status}}
              testId="acciones-plan-accion"
            />
            <p className="mt-0.5 text-xs text-muted-foreground">
              {m.responsable_nombre ?? t('investigacion.medidas.sinResponsable')} ·{' '}
              <span className="font-mono tabular-nums">{formatShortDate(m.target_date)}</span>
            </p>
          </li>
        ))}
      </ul>
    )
  }
  const arrancada = incidente.estado === 'en_investigacion' || incidente.estado === 'cerrado'
  return (
    <div className="space-y-2" data-testid="acciones-plan-vacio">
      <p className="text-sm text-muted-foreground">
        {t(arrancada ? 'incidentes.ficha.accionesPlanVacioEnCurso' : 'incidentes.ficha.accionesPlanVacioSinArrancar')}
      </p>
      {arrancada ? (
        <Button asChild size="sm" variant="outline" data-testid="acciones-plan-ir-medidas">
          <Link to={`/businesses/${businessId}/incidentes/${incidente.id}/investigacion?paso=medidas`}>
            {t('incidentes.ficha.accionesPlanIrMedidas')}
          </Link>
        </Button>
      ) : (
        <Hint testId="acciones-plan-siguiente-paso">{t('incidentes.ficha.accionesPlanSiguientePaso')}</Hint>
      )}
    </div>
  )
}

function ApartadoInvestigacion({titulo, children}: {titulo: string; children: ReactNode}) {
  return (
    <div>
      <p className="mb-2 font-mono text-[10px] font-semibold tracking-[0.12em] text-muted-foreground uppercase">
        {titulo}
      </p>
      {children}
    </div>
  )
}

export function FichaTab({
  incidente,
  businessId,
  readOnly,
  canOperate,
}: {
  incidente: Incidente
  businessId: string
  readOnly: boolean
  canOperate: boolean
}) {
  const {t} = useTranslation()
  const {data: inv, isLoading: invLoading} = useInvestigacion(businessId, incidente.id, canOperate)
  const {data: evidencia} = useInvestigacionEvidencia(businessId, incidente.id, canOperate)
  const {data: catalog} = useLesionesCatalog(businessId)
  const lesionLabels = new Map((catalog ?? []).map((c) => [c.code, c.label_es]))

  const gravedadDifiere =
    incidente.gravedad_derivada !== null && incidente.gravedad_derivada !== incidente.gravedad_reportada
  // La gravedad es magnitud: va por la rampa de U2 (lime, ámbar, rojo), no por la paleta de estado.
  const gravedadTone =
    incidente.gravedad_reportada === 'mortal' ? 'red' : incidente.gravedad_reportada === 'grave' ? 'amber' : 'lime'

  const bloquesIdentificacion: BloqueFicha[] = [
    {
      clave: 'que-paso',
      titulo: t('incidentes.ficha.bloques.quePaso'),
      testId: 'ficha-bloque-que-paso',
      filas: [
        {clave: 'tipo', etiqueta: t('incidentes.ficha.tipo'), valor: t(`incidentes.tipo.${incidente.tipo}`)},
        {
          clave: 'gravedad',
          etiqueta: t('incidentes.ficha.gravedad'),
          valor: (
            <CeldaEstado
              chip={
                <Tag tone={gravedadTone} data-testid="incidente-gravedad-ficha">
                  {gravedadChipLabel(t, incidente.gravedad_reportada)}
                </Tag>
              }
              porque={t(`incidentes.gravedad.${incidente.gravedad_reportada}`)}
              meta={
                gravedadDifiere ? (
                  <span data-testid="ficha-gravedad-derivada">
                    {t('incidentes.ficha.gravedadDerivada', {
                      gravedad: t(`incidentes.gravedad.${incidente.gravedad_derivada}`),
                    })}
                  </span>
                ) : undefined
              }
            />
          ),
        },
        {clave: 'descripcion', etiqueta: t('incidentes.ficha.descripcion'), valor: incidente.descripcion_hechos},
        ...(inv?.cronologia
          ? [{clave: 'cronologia', etiqueta: t('investigacion.cronologia.label'), valor: inv.cronologia}]
          : []),
        ...(inv?.testigos
          ? [{clave: 'testigos', etiqueta: t('investigacion.cronologia.testigos'), valor: inv.testigos}]
          : []),
        {clave: 'origen', etiqueta: t('incidentes.ficha.origen'), valor: t(`incidentes.origen.${incidente.origen}`)},
      ],
    },
    {
      clave: 'donde-cuando',
      titulo: t('incidentes.ficha.bloques.dondeCuando'),
      testId: 'ficha-bloque-donde-cuando',
      filas: [
        {
          clave: 'fecha_hora',
          etiqueta: t('incidentes.ficha.fechaHora'),
          valor: (
            <span className="font-mono tabular-nums">
              {formatShortDate(incidente.evento_fecha)}
              {incidente.evento_hora && ` · ${incidente.evento_hora}`}
            </span>
          ),
        },
        {
          clave: 'lugar',
          etiqueta: t('incidentes.ficha.lugar'),
          valor: incidente.lugar_nombre ?? incidente.lugar_libre ?? '—',
        },
      ],
    },
    {
      clave: 'quien',
      titulo: t('incidentes.ficha.bloques.quien'),
      testId: 'ficha-bloque-quien',
      filas: [
        {
          clave: 'afectado',
          etiqueta: t('incidentes.ficha.afectadoPrincipal'),
          valor: incidente.afectado_nombre ?? '—',
        },
      ],
    },
  ]

  return (
    <div className="space-y-4">
      <FichaDatos testId="ficha-identificacion" bloques={bloquesIdentificacion} />

      {incidente.tipo_lesion_codes.length > 0 && (
        <SectionCard testId="ficha-lesiones" title={t('incidentes.ficha.lesiones')} bodyClassName="p-5">
          {/* Sin tono (U2(b)): los códigos que sustentan la gravedad van por peso y con texto (U17). */}
          <div className="flex flex-wrap gap-1.5">
            {incidente.tipo_lesion_codes.map((code) => {
              const sustenta = incidente.gravedad_basis_codes.includes(code)
              return (
                <Tag
                  key={code}
                  tone="neutral"
                  className={sustenta ? 'font-bold ring-1 ring-border' : undefined}
                  title={sustenta ? t('incidentes.ficha.sustentaGravedad') : undefined}>
                  {lesionLabels.get(code) ?? code}
                  {sustenta && <span className="sr-only"> — {t('incidentes.ficha.sustentaGravedad')}</span>}
                </Tag>
              )
            })}
          </div>
        </SectionCard>
      )}

      <SectionCard testId="ficha-afectados" title={t('incidentes.ficha.afectados')} bodyClassName="p-5">
        <AfectadosManager
          businessId={businessId}
          incidenteId={incidente.id}
          afectados={inv?.afectados ?? []}
          readOnly={readOnly || !canOperate}
          isLoading={invLoading}
          principalNombre={incidente.afectado_nombre}
          principalEmployeeId={incidente.afectado_employee_id}
        />
      </SectionCard>

      {/* Solo para quien opera el SG-SST: para el resto cada botón respondería 403. */}
      {canOperate && (
        <>
          <SectionCard
            testId="ficha-investigacion"
            title={t('incidentes.ficha.investigacion')}
            actions={
              <Button asChild size="sm" variant="outline" data-testid="ficha-editar-investigacion">
                <Link to={`/businesses/${businessId}/incidentes/${incidente.id}/investigacion`}>
                  {t('incidentes.ficha.editarInvestigacion')}
                </Link>
              </Button>
            }
            bodyClassName="space-y-4 p-5">
            {inv ? (
              <>
                <ApartadoInvestigacion titulo={t('investigacion.paso.equipo')}>
                  <EquipoList equipo={inv.equipo} />
                </ApartadoInvestigacion>
                <ApartadoInvestigacion titulo={t('investigacion.paso.causas')}>
                  <CausasTree causas={inv.causas} />
                </ApartadoInvestigacion>
              </>
            ) : (
              <Skeleton className="h-24 w-full" />
            )}
          </SectionCard>

          <SectionCard
            testId="incidente-acciones-plan"
            title={t('incidentes.ficha.accionesPlan')}
            actions={
              <PorQue
                label={t('incidentes.ficha.accionesPlanPorqueLabel')}
                testId="acciones-plan-porque"
                contenidoTestId="acciones-plan-porque-contenido">
                {t('incidentes.ficha.accionesPlanTip')}
              </PorQue>
            }
            bodyClassName="p-5">
            {/* Ancla de la guarda E2E: cubre la sección entera sin atrapar el disparador de la cabecera. */}
            <div data-testid="acciones-plan-cuerpo">
              <AccionesPlanLigadas
                incidente={incidente}
                businessId={businessId}
                medidas={inv?.medidas}
                cargando={invLoading}
              />
            </div>
          </SectionCard>

          <SectionCard testId="ficha-evidencias" title={t('incidentes.ficha.evidencias')} bodyClassName="p-5">
            {/* Se monta siempre: `inv` puede ser `undefined` y el subir queda apagado hasta que llegue. */}
            <InvestigacionEvidencia
              businessId={businessId}
              incidenteId={incidente.id}
              investigacionId={inv?.id ?? ''}
              items={evidencia ?? []}
              editable={!readOnly && !!inv}
            />
          </SectionCard>

          <SectionCard testId="ficha-documentos" title={t('incidentes.ficha.documentos')} bodyClassName="p-5">
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="outline"
                className="min-h-11 sm:min-h-0"
                data-testid="informe-descargar"
                onClick={() => downloadBlob(informeUrl(businessId, incidente.id), 'informe-1401.docx')}>
                {t('investigacion.informe.descargar')}
              </Button>
              {incidente.tipo === 'accidente' && (
                <Button
                  size="sm"
                  variant="outline"
                  className="min-h-11 sm:min-h-0"
                  data-testid="furat-documento-descargar"
                  onClick={() => downloadBlob(furatDocumentoUrl(businessId, incidente.id), 'furat.docx')}>
                  {t('incidentes.ficha.descargarFurat')}
                </Button>
              )}
            </div>
          </SectionCard>
        </>
      )}
    </div>
  )
}
